import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";
import _ from "lodash";

import { processWidgetData } from "../../../helpers/reports.helper";
import {
  DataKeys,
  DrilldownKeys,
  widgetKeys,
} from "../../../helpers/reports.helper.types";
import { WidgetHeader } from "../../../components/charts/widgets/widgetHeader/WidgetHeader";
import Widget from "../../../components/charts/Widget";
import { getReportsPeriod } from "../../../store/reports/reports.selectors";
import { ChartTitle } from "../Dashboard";
import { WidgetLineChart } from "../../../components/charts/widgets/widgetLineChart";
import {
  getWidgetData,
  isWidgetLoadFailed,
  isWidgetLoading,
} from "../../../store/reports/reducers/widgets/widgets.selectors";
import { loadWidgetReport } from "../../../store/reports/reducers/widgets/widgets.actions";

import "./WidgetsSection.scss";
import { WidgetErrorSummary } from "../../../components/charts/widgets/widgetSummary/WidgetErrorSummary";
import { WidgetSuccessSummary } from "../../../components/charts/widgets/widgetSummary/WidgetSuccessSummary";
import { WidgetsTimePeriodSelector } from "./WidgetsTimePeriodSelector";
import { Mobile, NotMobile } from "../../../hocs/Responsive";

const WIDGETS = {
  [DataKeys.webfilter]: {
    icon: "globe",
    header: <WidgetHeader id={DrilldownKeys.Webfilter} />,
    drilldown: DrilldownKeys.Webfilter,
    id: DrilldownKeys.Webfilter,
  },
  [DataKeys.malware]: {
    icon: "bug",
    header: <WidgetHeader id={DrilldownKeys.Malware} />,
    drilldown: DrilldownKeys.Malware,
    id: DrilldownKeys.Malware,
  },
  [DataKeys.botnet]: {
    icon: "ban",
    header: <WidgetHeader id={DrilldownKeys.Botnet} />,
    drilldown: DrilldownKeys.Botnet,
    id: DrilldownKeys.Botnet,
  },
};

const WidgetContainer = ({ widget }) => {
  const dispatch = useDispatch();
  const period = useSelector(getReportsPeriod);
  const reports = useSelector(getWidgetData(widget), _.isEqual);
  const isLoading = useSelector(isWidgetLoading(widget));
  const isFailure = useSelector(isWidgetLoadFailed(widget));

  const fetchWidgetReport = useCallback(() => {
    dispatch(loadWidgetReport(widget, { period }));
  }, [period, widget, dispatch]);

  useEffect(() => {
    fetchWidgetReport();
  }, [fetchWidgetReport]);

  const { drilldown, header, icon, id } = WIDGETS[widget];
  const data = useMemo(() => {
    return processWidgetData(reports, period);
  }, [reports, period]);

  const count = useMemo(
    () => _.reduce(data, (sum, item) => sum + item.value, 0),
    [data]
  );

  return (
    <Widget className={id} drilldown={drilldown} isLoading={isLoading}>
      {isFailure ? (
        <WidgetErrorSummary
          header={header}
          icon={icon}
          onRefreshClick={fetchWidgetReport}
        />
      ) : (
        <WidgetSuccessSummary
          count={count}
          header={header}
          icon={icon}
          id={id}
        />
      )}
      <NotMobile>
        <WidgetLineChart data={data} id={id} />
      </NotMobile>
      <Mobile>{!isFailure && <WidgetLineChart data={data} id={id} />}</Mobile>
    </Widget>
  );
};

const Widgets = () => {
  return (
    <div className="widgets">
      {widgetKeys.map((key) => (
        <WidgetContainer key={key} widget={key} />
      ))}
    </div>
  );
};

export const WidgetsSection = () => (
  <div className="chart-section">
    <h2 className="homepage-reports__title">
      <FormattedMessage id="statistic" />
    </h2>
    <WidgetsTimePeriodSelector />
    <section>
      <ChartTitle title={<FormattedMessage id="blocked_by_1" />} />
      <Widgets />
    </section>
  </div>
);
